// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import type { Client } from '../../../../types/client';
import type {
  Gender,
  MaritalStatusInput,
  MaxFundingPayload,
  MaxFundingScenarioInputs,
  MaxFundingSubScenario,
} from '../../../../types/calculationResult';

export const DEFAULT_RETIREMENT_AGE = 65;
export const RETIREMENT_AGE_MIN = 60;
export const RETIREMENT_AGE_MAX = 75;

export function retirementAgeOptions(): Array<{ value: string; text: string }> {
  const options: Array<{ value: string; text: string }> = [];
  for (let age = RETIREMENT_AGE_MIN; age <= RETIREMENT_AGE_MAX; age++) {
    options.push({ value: String(age), text: String(age) });
  }
  return options;
}

export function buildDefaultScenarioInputs(
  client?: Client,
  retirementAge: number = DEFAULT_RETIREMENT_AGE,
): MaxFundingScenarioInputs {
  return {
    retirementAge,
    currentGrossPayeEarnings: 0,
    yearsWithCurrentEmployerToDate: 0,
    existingPension: false,
    defBenPrevTotal: 0,
    defConPrevTotal: 0,
    defBenCurTotal: 0,
    defConCurTotal: 0,
    gender: (client?.gender as Gender | undefined) ?? 'MALE',
    maritalStatus: (client?.maritalStatus as MaritalStatusInput | undefined) ?? 'SINGLE',
    dateOfBirth: client?.dateOfBirth ?? '',
  };
}

export function buildDefaultSubScenario(
  client?: Client,
  retirementAge: number = DEFAULT_RETIREMENT_AGE,
): MaxFundingSubScenario {
  return {
    retirementAge,
    inputs: buildDefaultScenarioInputs(client, retirementAge),
  };
}

export function buildEmptyMaxFundingPayload(client?: Client): MaxFundingPayload {
  return {
    scenarios: [buildDefaultSubScenario(client)],
  };
}

/**
 * Map a v1 inputs object (existingDB/existingDC) onto the v2 4-field shape.
 * Treats legacy values as Previous-employment (matches the engine fallback).
 */
type LegacyInputs = MaxFundingScenarioInputs & {
  existingDB?: number;
  existingDC?: number;
};

export function migrateLegacyInputs(raw: LegacyInputs): MaxFundingScenarioInputs {
  const hasV2 =
    raw.defBenPrevTotal !== undefined ||
    raw.defBenCurTotal !== undefined ||
    raw.defConPrevTotal !== undefined ||
    raw.defConCurTotal !== undefined;
  if (hasV2) {
    return {
      ...raw,
      defBenPrevTotal: raw.defBenPrevTotal ?? 0,
      defConPrevTotal: raw.defConPrevTotal ?? 0,
      defBenCurTotal: raw.defBenCurTotal ?? 0,
      defConCurTotal: raw.defConCurTotal ?? 0,
    };
  }
  return {
    ...raw,
    defBenPrevTotal: raw.existingDB ?? 0,
    defConPrevTotal: raw.existingDC ?? 0,
    defBenCurTotal: 0,
    defConCurTotal: 0,
  };
}

export function migrateLegacyPayload(payload: MaxFundingPayload): MaxFundingPayload {
  return {
    ...payload,
    scenarios: payload.scenarios.map((s) => ({
      ...s,
      inputs: migrateLegacyInputs(s.inputs as LegacyInputs),
    })),
  };
}

export function formatCurrencyEUR(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `€${value.toLocaleString('en-IE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// Standard Fund Threshold (Irish Revenue) — must stay in sync with backend SftThreshold.java
export const SFT_2025_OR_EARLIER = 2_000_000;
export const SFT_2026 = 2_200_000;
export const SFT_2027 = 2_400_000;
export const SFT_2028 = 2_600_000;
export const SFT_2029_OR_LATER = 2_800_000;

export function sftForRetirementYear(year: number): number {
  if (year <= 2025) return SFT_2025_OR_EARLIER;
  if (year === 2026) return SFT_2026;
  if (year === 2027) return SFT_2027;
  if (year === 2028) return SFT_2028;
  return SFT_2029_OR_LATER;
}

export function retirementYearFromInputs(
  dateOfBirth: string | undefined,
  retirementAge: number | undefined,
): number | null {
  if (!dateOfBirth || retirementAge == null || !Number.isFinite(retirementAge)) return null;
  const birthYear = new Date(dateOfBirth).getFullYear();
  if (!Number.isFinite(birthYear)) return null;
  return birthYear + retirementAge;
}

export function sftForScenario(
  dateOfBirth: string | undefined,
  retirementAge: number | undefined,
): number {
  const year = retirementYearFromInputs(dateOfBirth, retirementAge);
  return year == null ? SFT_2029_OR_LATER : sftForRetirementYear(year);
}
